import React from 'react'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { URL } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logInfo } from '@sputnik/utils/loggerUtils'

export interface AccountCreatedBannerProps extends Pick<
  GetCreditApplicationDetails_creditApplication_CreditApplication,
  'authorizedSigner'
> {
  salesforceCreditId: string
  hasCreatedPassword: boolean
}
// this banner will show after account is built. The login will always show if it's built and after migration success
const AccountCreatedBanner = ({
  salesforceCreditId,
  authorizedSigner,
  hasCreatedPassword,
}: AccountCreatedBannerProps) => {
  const { track } = useWebTracking()
  const { isMigration, isLoadingCreditAppTypes } = useGetCreditAppTypes({ salesforceCreditId })
  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail: authorizedSigner?.email || null,
  })
  const authSignerInvitationToken = authorizedSigner?.accountSetupInvitation?.token

  const needsToSetupAccount = authSignerInvitationToken !== null && !hasCreatedPassword && !isMigration

  useDidMount(() => {
    track('se_signup__application_dashboard_action_needed_account_created__presented')
  })

  const navigateToSELogin = () => {
    logInfo('goToSpendAndExpense', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Sending user to path: ${URL.DIVVY_PROD}`,
      },
      eventName: 'navigateToSELogin',
    })
    window.open(URL.DIVVY_PROD, '_blank', 'noopener, noreferrer')
  }

  const navigateToSEAccountSetup = () => {
    logInfo('goToSpendAndExpense', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Sending user to path: ${URL.DIVVY_PROD_PASSWORD_SETUP}/${authSignerInvitationToken}`,
      },
      eventName: 'navigateToSEAccountSetup',
    })
    window.open(`${URL.DIVVY_PROD_PASSWORD_SETUP}/${authSignerInvitationToken}`, '_blank', 'noopener, noreferrer')
  }

  const needsToSetupAccountText = needsToSetupAccount ? (
    <FormattedMessage
      defaultMessage='Now let’s set up your username and password. <link>Set up login</link>'
      id='sputnik.AccountCreatedBanner__dezVS5'
      values={{
        link: (chunks) => (
          <TextLink
            disabled={isLoadingCreditAppTypes}
            size={TextLinkSizes.SMALL}
            onClick={() => {
              track('se_signup__application_dashboard_action_needed_set_up_login__fired')
              navigateToSEAccountSetup()
            }}
          >
            {chunks}
          </TextLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage
      defaultMessage='You’re all set to get started. <link>Log in</link>'
      id='sputnik.AccountCreatedBanner__hX1g8h'
      values={{
        link: (chunks) => (
          <TextLink
            disabled={isLoadingCreditAppTypes}
            size={TextLinkSizes.SMALL}
            onClick={() => {
              track('se_signup__application_dashboard_action_needed_log_in__fired')
              navigateToSELogin()
            }}
          >
            {chunks}
          </TextLink>
        ),
      }}
    />
  )

  const nonAuthSignerText = (
    <FormattedMessage
      defaultMessage='Reach out to your company admin with questions.'
      id='sputnik.AccountCreatedBanner__MD5jFz'
    />
  )

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Success}
        subtext={isLoggedInUserAuthorizedSigner ? needsToSetupAccountText : nonAuthSignerText}
        title={
          <FormattedMessage
            defaultMessage='Account created'
            id='sputnik.AccountCreatedBanner__1JRfgv'
          />
        }
      />
    </div>
  )
}

export default AccountCreatedBanner
